import { escapeHtml } from '../util/escapeHtml.js';
/**
 * Clickable perk chips. Call `attachPerkInfoToggle(el, slug)` on any element that
 * names a perk; clicking (or Enter/Space) opens a small modal describing the
 * perk's effect, read from the shared PERK_CATALOG. Mirrors the item-info modal
 * pattern, but the data is static so there's no fetch.
 */
import { PERK_CATALOG, perkName } from '@rex-mortis/shared';

let backdrop: HTMLDivElement | null = null;
let modal: HTMLDivElement | null = null;

function ensureStyles(): void {
  if (document.getElementById('pkm-styles')) return;
  const style = document.createElement('style');
  style.id = 'pkm-styles';
  style.textContent = STYLES;
  document.head.appendChild(style);
}

function ensureModal(): void {
  if (backdrop && modal) return;
  backdrop = document.createElement('div');
  backdrop.className = 'pkm-backdrop';
  modal = document.createElement('div');
  modal.className = 'pkm-modal';
  backdrop.appendChild(modal);
  document.body.appendChild(backdrop);
  backdrop.addEventListener('click', (e) => {
    if (e.target === backdrop) close();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') close();
  });
}

function close(): void {
  backdrop?.classList.remove('open');
}

function open(slug: string): void {
  ensureStyles();
  ensureModal();
  const info = PERK_CATALOG[slug];
  const name = perkName(slug);
  const kindLabel =
    info?.kind === 'legacy'
      ? (info.oneShot
          ? 'Legacy — granted once (course is once in a lifetime)'
          : 'Legacy — stacks for your first 5 completions')
      : 'One-time unlock';
  modal!.innerHTML = `
    <button class="pkm-close" aria-label="Close">×</button>
    <h2 class="pkm-name">${escapeHtml(name)}</h2>
    <div class="pkm-tag">${escapeHtml(kindLabel)}</div>
    <p class="pkm-effect">${escapeHtml(info?.effect ?? 'A scholarly boon. Its effect is not yet recorded.')}</p>
    ${info?.source ? `<p class="pkm-source">Earned from <strong>${escapeHtml(info.source)}</strong> at the Scholarium.</p>` : ''}
  `;
  modal!.querySelector<HTMLButtonElement>('.pkm-close')!.addEventListener('click', close);
  backdrop!.classList.add('open');
}

export function attachPerkInfoToggle(el: HTMLElement, slug: string): void {
  ensureStyles();
  el.classList.add('pkm-toggle');
  el.setAttribute('role', 'button');
  el.setAttribute('tabindex', '0');
  el.setAttribute('title', `${perkName(slug)} — tap for details`);
  const handler = (e: Event): void => {
    e.preventDefault();
    e.stopPropagation();
    open(slug);
  };
  el.addEventListener('click', handler);
  el.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') handler(e);
  });
}

const STYLES = `
  .pkm-toggle {
    cursor: pointer;
    text-decoration: none;
    border-bottom: 1px dotted currentColor;
  }
  .pkm-toggle::after { content: ' \\25B8'; font-size: 0.85em; opacity: 0.7; }
  .pkm-backdrop {
    position: fixed; inset: 0;
    background: rgba(28, 17, 8, 0.55);
    backdrop-filter: blur(2px);
    z-index: 400; opacity: 0; pointer-events: none;
    transition: opacity 0.18s ease;
    display: flex; align-items: center; justify-content: center;
  }
  .pkm-backdrop.open { opacity: 1; pointer-events: auto; }
  .pkm-modal {
    width: min(440px, 92vw);
    background: var(--c-parchment);
    background-image: var(--parchment-texture);
    border: 1px solid var(--c-rule-strong);
    border-radius: var(--radius-lg);
    box-shadow: 0 0 0 1px var(--c-gold), 0 20px 60px rgba(0, 0, 0, 0.5);
    padding: var(--s-4) var(--s-5);
    position: relative;
  }
  .pkm-close {
    position: absolute; top: 6px; right: 10px;
    background: transparent; border: 0; cursor: pointer;
    font-size: 1.6rem; line-height: 1; color: var(--c-ink-soft);
  }
  .pkm-close:hover { color: var(--c-oxblood); }
  .pkm-name {
    margin: 0 0 4px 0;
    font-family: var(--font-display);
    color: var(--c-oxblood);
  }
  .pkm-tag {
    display: inline-block;
    font-size: 0.75rem; letter-spacing: 0.03em;
    color: var(--c-ink-soft);
    border: 1px solid var(--c-rule-soft);
    border-radius: 999px;
    padding: 1px 8px;
    margin-bottom: var(--s-2);
  }
  .pkm-effect { margin: 0 0 var(--s-2) 0; font-size: 1rem; }
  .pkm-source { margin: 0; font-style: italic; color: var(--c-ink-soft); font-size: 0.9rem; }
`;
